import {
	IClientConfigs,
	IJobPromotion,
	Job,
	JobSelectedVehicle,
	VehicleType,
} from '~/shared';

import { notifyOnError } from '~/rage/decorators';
import { IObservable, Subject } from '~/rage/utils';
import { GameConfigService } from '../GameConfigService';
import { IJobTask, IJobTasksService } from '../JobTasksService';
import { RpcService } from '../RpcService';
import { VarsService } from '../VarsService';
import { IJobsService, JobRentVehicleConfig } from './jobsServiceTypes';

export class JobsService implements IJobsService {
	private readonly playerAtWork = new Subject<boolean>(false);

	private readonly withRentVehicles = new Subject<boolean>(false);
	private readonly withOwnedVehicles = new Subject<boolean>(false);
	private readonly rentVehicles = new Subject<JobRentVehicleConfig[]>([]);
	private readonly allowedVehicleTypes = new Subject<VehicleType[]>([]);

	private readonly promotions = new Subject<IJobPromotion[]>([]);

	constructor(
		private readonly rpc: RpcService,
		private readonly vars: VarsService,
		gameConfig: GameConfigService,
		private readonly jobTasks: IJobTasksService,
	) {
		this.getActiveJobId().subscribe((activeJobId) => {
			const openedJobId = this.getOpenedJobId().getValue();
			this.playerAtWork.next(activeJobId === openedJobId);
		});

		this.getOpenedJobId().subscribe((openedJobId) => {
			const activeJobId = this.getActiveJobId().getValue();
			this.playerAtWork.next(openedJobId === activeJobId);

			this.updateVehicleSelector(gameConfig.configs);
		});

		this.vars.get('promotions', []).subscribe((promotions) => {
			this.promotions.next(promotions);
		});

		gameConfig.getConfigs().subscribe(this.updateVehicleSelector.bind(this));
	}

	private updateVehicleSelector(config: Partial<IClientConfigs>): void {
		if (!config?.jobTaxi) {
			return;
		}

		const openedJobId = this.getOpenedJobId().getValue();
		if (!openedJobId) {
			return;
		}

		const withRentVehicles = openedJobId === Job.Taxi;
		this.withRentVehicles.next(withRentVehicles);

		const withOwnedVehicles = openedJobId === Job.Taxi;
		this.withOwnedVehicles.next(withOwnedVehicles);

		const rentVehicles =
			config.jobTaxi.rentVehicles.map((e) => ({
				...e,
				jobId: Job.Taxi,
			})) ?? [];
		this.rentVehicles.next(rentVehicles);

		this.allowedVehicleTypes.next(config.jobTaxi.allowedTypes ?? []);
	}

	public getOpenedJobId(): IObservable<Job | null> {
		return this.vars.get('openedJobId', null);
	}

	public getActiveJobId(): IObservable<Job | null> {
		return this.vars.get('activeJobId', null);
	}

	public getPlayerAtWork(): IObservable<boolean> {
		return this.playerAtWork;
	}

	public getRentVehicles(): IObservable<JobRentVehicleConfig[]> {
		return this.rentVehicles;
	}

	public getAllowedVehicleTypes(): IObservable<VehicleType[]> {
		return this.allowedVehicleTypes;
	}

	public getWithRentVehicles(): IObservable<boolean> {
		return this.withRentVehicles;
	}

	public getWithOwnedVehicles(): IObservable<boolean> {
		return this.withOwnedVehicles;
	}

	public getJobPromotions(): IObservable<IJobPromotion[]> {
		return this.promotions;
	}

	public getTasks(): IObservable<IJobTask[]> {
		const openedJobId = this.getOpenedJobId().getValue();
		if (!openedJobId) {
			return new Subject<IJobTask[]>([]);
		}

		return this.jobTasks.getJobTasks(openedJobId);
	}

	public getTasksByJob(jobId: Job): IObservable<IJobTask[]> {
		return this.jobTasks.getJobTasks(jobId);
	}

	public getTasksExpireAt(): IObservable<number> {
		return this.jobTasks.getTasksExpireAt();
	}

	@notifyOnError
	public async startWorkShift(vehicle?: JobSelectedVehicle): Promise<void> {
		const id = this.getOpenedJobId().getValue();
		await this.rpc.invokeServer('jobs:startSession', { id, vehicle });

		this.exitMenu();
	}

	@notifyOnError
	public async stopWorkShift(): Promise<void> {
		const id = this.getOpenedJobId().getValue();
		return await this.rpc.invokeServer('jobs:stopSession', { id });
	}

	public async exitMenu(): Promise<void> {
		return await this.rpc.invokeClient('jobs:exit');
	}
}
